/**
 * SA 「発行済請求書」タブ (v3.2.14 / Phase 3a)。
 *
 * 自分が発行した commission_invoices を一覧表示。各行から:
 *  - 公開URLコピー
 *  - 詳細ページ (内訳閲覧 / PDF ダウンロード) へ遷移
 *  - 取消 (status='sent' のみ)
 */
import type { CommissionInvoice } from '@goenhub/shared';
import { COMMISSION_INVOICE_STATUS_LABELS } from '@goenhub/shared';
import { ClipboardCopy, FileText, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/hooks/useAuth';
import {
  invoiceLineBreakdown,
  useCancelCommissionInvoice,
  useMyCommissionInvoices,
} from '@/hooks/useCommissionInvoices';

function fmtDate(v: unknown): string {
  if (!v) return '-';
  if (typeof v === 'string') return new Date(v).toLocaleDateString('ja-JP');
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleDateString('ja-JP');
  }
  return '-';
}

function statusBadgeVariant(s: CommissionInvoice['status']) {
  if (s === 'paid') return 'approved' as const;
  if (s === 'sent') return 'review' as const;
  if (s === 'cancelled') return 'danger' as const;
  return 'default' as const;
}

export function SaInvoicesTab() {
  const { user } = useAuth();
  const invoices = useMyCommissionInvoices(user?.uid);
  const cancel = useCancelCommissionInvoice();

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('公開URLをコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  const handleCancel = async (id: string, number: string) => {
    if (!confirm(`請求書「${number}」を取り消します。\n含まれる報酬レコードは「未申請」に、個別説明代行は「未請求」に戻ります。よろしいですか？`)) return;
    try {
      await cancel.mutateAsync({ invoiceId: id });
      toast.success('請求書を取り消しました');
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  if (invoices.isLoading) return <Spinner />;
  if (!invoices.data || invoices.data.length === 0) {
    return (
      <EmptyState
        icon={FileText}
        title="発行済請求書がありません"
        description="「請求可能」タブから複数レコードを選択して発行できます。"
      />
    );
  }

  return (
    <div className="rounded-md border border-[var(--border)] overflow-hidden">
      <div className="grid grid-cols-[140px_1.5fr_90px_80px_120px_130px] gap-2 px-3 py-2 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
        <div>請求番号</div>
        <div>対象期間 / 件数</div>
        <div>発行日</div>
        <div className="text-center">状態</div>
        <div className="text-right">合計</div>
        <div className="text-center">操作</div>
      </div>
      {invoices.data.map((inv) => {
        const publicUrl = `${window.location.origin}/c/sa-invoice/${inv.publicToken}`;
        const { salesCount, briefingCount } = invoiceLineBreakdown(inv);
        return (
          <div
            key={inv.id}
            className="grid grid-cols-[140px_1.5fr_90px_80px_120px_130px] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)]"
          >
            <div className="font-mono tabular-nums">{inv.invoiceNumber}</div>
            <div className="min-w-0">
              <div className="truncate">{inv.periodLabel ?? '(期間ラベルなし)'}</div>
              <div className="text-[10px] text-[var(--text-muted)]">
                報酬 {salesCount} 件
                {briefingCount > 0 ? ` ・ 個別説明 ${briefingCount} 件` : ''}
              </div>
            </div>
            <div className="text-[var(--text-muted)] tabular-nums">{fmtDate(inv.issuedAt)}</div>
            <div className="text-center">
              <Badge variant={statusBadgeVariant(inv.status)}>
                {COMMISSION_INVOICE_STATUS_LABELS[inv.status]}
              </Badge>
            </div>
            <div className="text-right tabular-nums font-semibold">
              ¥{inv.totalIncludingTax.toLocaleString()}
            </div>
            <div className="flex items-center justify-center gap-1.5">
              <button
                type="button"
                onClick={() => handleCopy(publicUrl)}
                title="公開URLコピー"
                className="size-7 inline-flex items-center justify-center rounded-sm hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
              >
                <ClipboardCopy className="size-3.5" />
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="請求書を開く"
                className="size-7 inline-flex items-center justify-center rounded-sm hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
              >
                <FileText className="size-3.5" />
              </a>
              {inv.status === 'sent' && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCancel(inv.id, inv.invoiceNumber)}
                  disabled={cancel.isPending}
                  title="取消 (records は未申請に戻る)"
                >
                  {cancel.isPending ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <XCircle className="size-3" />
                  )}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
